// src/components/dashboard/shared/DocumentsManager.js - VERSION AVEC DOSSIERS
import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  updateDoc, 
  doc, 
  orderBy, 
  onSnapshot,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { getAuth } from 'firebase/auth';
import {
  Upload,
  Search,
  Grid,
  List,
  Download,
  Eye,
  Trash2,
  Edit,
  FileText,
  Image,
  File,
  Star,
  StarOff,
  FolderOpen,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  CloudUpload,
  Tag,
  Plus,
  Folder,
  ArrowLeft,
  Home,
  ChevronRight
} from 'lucide-react';

// Catégories avec icônes et couleurs
const DOCUMENT_CATEGORIES = {
  contracts: { name: 'Contrats', icon: FileText, color: 'blue' },
  photos: { name: 'Photos', icon: Image, color: 'green' },
  invoices: { name: 'Factures', icon: FileText, color: 'yellow' },
  certificates: { name: 'Certificats', icon: FileText, color: 'purple' },
  technical: { name: 'Technique', icon: File, color: 'indigo' },
  reports: { name: 'Rapports', icon: FileText, color: 'red' },
  other: { name: 'Autres', icon: File, color: 'gray' }
};

const DocumentsManager = ({ 
  dossierId, 
  isAdmin = false, 
  canEdit = false, 
  showInternalDocs = false 
}) => {
  const auth = getAuth();
  
  // États principaux
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  // États de l'interface
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showMyDocsOnly, setShowMyDocsOnly] = useState(false);
  
  // États pour navigation par dossiers
  const [currentView, setCurrentView] = useState('folders'); // 'folders' | 'documents' | 'all'
  const [currentFolder, setCurrentFolder] = useState(null);
  
  // États pour les modales
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  
  // États pour upload avec sélection de catégorie
  const [dragActive, setDragActive] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [selectedCategoryForUpload, setSelectedCategoryForUpload] = useState('other');
  const fileInputRef = useRef(null);

  // Fonction de debug
  const debugLog = (action, data, error = null) => {
    console.group(`🔧 DocumentsManager - ${action}`);
    console.log('Data:', data);
    if (error) {
      console.error('Error:', error);
      console.log('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
    console.groupEnd();
  };

  // Chargement des documents
  useEffect(() => {
    if (!dossierId) {
      debugLog('INIT ERROR', 'Pas de dossierId fourni');
      setError('ID de dossier manquant');
      setLoading(false);
      return;
    }

    debugLog('LOADING DOCUMENTS', { dossierId, showInternalDocs });

    const documentsQuery = query(
      collection(db, 'documents'),
      where('dossierId', '==', dossierId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      documentsQuery, 
      (snapshot) => {
        try {
          const documentsList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          debugLog('DOCUMENTS LOADED', { 
            count: documentsList.length, 
            documents: documentsList.map(d => ({ id: d.id, name: d.name, uploadedBy: d.uploadedBy }))
          });
          
          // Filtrer les documents internes si nécessaire
          const filteredDocs = showInternalDocs 
            ? documentsList 
            : documentsList.filter(doc => doc.visibility !== 'internal');
          
          setDocuments(filteredDocs);
          setLoading(false);
          setError(null);
        } catch (err) {
          debugLog('DOCUMENTS LOADING ERROR', null, err);
          setError(`Erreur lors du chargement: ${err.message}`);
          setLoading(false);
        }
      },
      (err) => {
        debugLog('DOCUMENTS QUERY ERROR', null, err);
        setError(`Erreur de requête: ${err.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [dossierId, showInternalDocs]);

  // Calculer les statistiques par catégorie
  const categoryStats = Object.keys(DOCUMENT_CATEGORIES).reduce((stats, category) => {
    stats[category] = documents.filter(doc => doc.category === category).length;
    return stats;
  }, {});

  // Détection automatique de la catégorie (fallback)
  const detectCategory = (file) => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type;
    
    if (fileName.includes('contrat') || fileName.includes('devis')) return 'contracts';
    if (fileName.includes('facture') || fileName.includes('invoice')) return 'invoices';
    if (fileName.includes('certificat') || fileName.includes('conformite')) return 'certificates';
    if (fileName.includes('rapport') || fileName.includes('production')) return 'reports';
    if (fileName.includes('technique') || fileName.includes('schema')) return 'technical';
    if (fileType.startsWith('image/')) return 'photos';
    
    return 'other';
  };

  // Upload de fichier avec catégorie sélectionnée
  const uploadFile = async (file, category) => {
    debugLog('UPLOAD START', { 
      fileName: file.name, 
      fileSize: file.size, 
      fileType: file.type,
      category,
      dossierId,
      userId: auth.currentUser?.uid,
      userEmail: auth.currentUser?.email
    });

    if (!auth.currentUser) {
      const error = new Error('Utilisateur non connecté');
      debugLog('UPLOAD ERROR', null, error);
      setError('Vous devez être connecté pour uploader des fichiers');
      return false;
    }

    if (!dossierId) {
      const error = new Error('ID de dossier manquant');
      debugLog('UPLOAD ERROR', null, error);
      setError('ID de dossier manquant');
      return false;
    }

    try {
      // 1. Créer la référence Storage
      const fileExtension = file.name.split('.').pop();
      const uniqueName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExtension}`;
      const filePath = `documents/${dossierId}/${uniqueName}`;
      const storageRef = ref(storage, filePath);
      
      debugLog('STORAGE REF CREATED', { filePath, uniqueName });

      // 2. Upload vers Storage
      debugLog('UPLOADING TO STORAGE', 'Début upload...');
      const uploadResult = await uploadBytes(storageRef, file);
      debugLog('STORAGE UPLOAD SUCCESS', { 
        bucket: uploadResult.metadata.bucket,
        fullPath: uploadResult.metadata.fullPath,
        size: uploadResult.metadata.size
      });

      // 3. Obtenir l'URL de téléchargement
      const downloadURL = await getDownloadURL(uploadResult.ref);
      debugLog('DOWNLOAD URL OBTAINED', { downloadURL });

      // 4. Préparer les données Firestore avec la catégorie sélectionnée
      const docData = {
        dossierId,
        name: file.name,
        originalName: file.name,
        uniqueName,
        url: downloadURL,
        path: filePath,
        size: file.size,
        type: file.type,
        category: category || detectCategory(file), // Utiliser la catégorie sélectionnée
        visibility: 'public',
        uploadedBy: auth.currentUser.uid,
        uploadedByEmail: auth.currentUser.email,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        starred: false,
        downloadCount: 0
      };

      debugLog('FIRESTORE DATA PREPARED', docData);

      // 5. Sauvegarder dans Firestore
      const docRef = await addDoc(collection(db, 'documents'), docData);
      debugLog('FIRESTORE SAVE SUCCESS', { docId: docRef.id });

      return true;

    } catch (error) {
      debugLog('UPLOAD ERROR', null, error);
      
      // Messages d'erreur détaillés
      let errorMessage = 'Erreur inconnue lors de l\'upload';
      
      if (error.code) {
        switch (error.code) {
          case 'storage/unauthorized':
            errorMessage = 'Permissions insuffisantes pour l\'upload';
            break;
          case 'storage/quota-exceeded':
            errorMessage = 'Quota de stockage dépassé';
            break;
          case 'storage/invalid-format':
            errorMessage = 'Format de fichier non supporté';
            break;
          case 'permission-denied':
            errorMessage = 'Permissions Firestore insuffisantes';
            break;
          case 'unauthenticated':
            errorMessage = 'Utilisateur non authentifié';
            break;
          default:
            errorMessage = `Erreur ${error.code}: ${error.message}`;
        }
      } else {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return false;
    }
  };

  // Gestion de l'upload multiple avec modal de catégorie
  const handleFileUpload = (files) => {
    if (!files || files.length === 0) return;
    
    const filesArray = Array.from(files);
    setPendingFiles(filesArray);
    setShowCategoryModal(true);
    
    // Auto-détecter la catégorie du premier fichier
    if (filesArray.length > 0) {
      const detectedCategory = detectCategory(filesArray[0]);
      setSelectedCategoryForUpload(detectedCategory);
    }
  };

  // Confirmer l'upload avec la catégorie sélectionnée
  const confirmUpload = async () => {
    setUploading(true);
    setError(null);
    setShowCategoryModal(false);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const file of pendingFiles) {
      const success = await uploadFile(file, selectedCategoryForUpload);
      if (success) {
        successCount++;
      } else {
        errorCount++;
      }
    }
    
    // Messages de résultat
    if (successCount > 0 && errorCount === 0) {
      alert(`✅ ${successCount} fichier(s) uploadé(s) avec succès !`);
    } else if (successCount > 0 && errorCount > 0) {
      alert(`⚠️ ${successCount} fichier(s) uploadé(s), ${errorCount} erreur(s)`);
    } else if (errorCount > 0) {
      alert(`❌ Échec de l'upload de ${errorCount} fichier(s)`);
    }
    
    setPendingFiles([]);
    setUploading(false);
  };

  // Annuler l'upload
  const cancelUpload = () => {
    setPendingFiles([]);
    setShowCategoryModal(false);
  };

  // Vérifier si l'utilisateur peut supprimer un document
  const canDeleteDocument = (document) => {
    return isAdmin || document.uploadedBy === auth.currentUser?.uid;
  };

  // Supprimer un document avec vérification des permissions
  const deleteDocument = async (document) => {
    // Vérifier les permissions
    if (!canDeleteDocument(document)) {
      alert('❌ Vous ne pouvez supprimer que vos propres documents');
      return;
    }
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${document.name}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    debugLog('DELETE START', { 
      documentId: document.id, 
      path: document.path,
      canDelete: canDeleteDocument(document),
      currentUser: auth.currentUser?.uid,
      documentOwner: document.uploadedBy
    });
    
    try {
      // Supprimer de Storage
      if (document.path) {
        await deleteObject(ref(storage, document.path));
        debugLog('STORAGE DELETE SUCCESS', document.path);
      }
      
      // Supprimer de Firestore
      await deleteDoc(doc(db, 'documents', document.id));
      debugLog('FIRESTORE DELETE SUCCESS', document.id);
      
      alert(`✅ Document "${document.name}" supprimé avec succès !`);
    } catch (error) {
      debugLog('DELETE ERROR', null, error);
      alert(`❌ Erreur lors de la suppression: ${error.message}`);
    }
  };

  // Marquer comme favori
  const toggleStar = async (document) => {
    try {
      await updateDoc(doc(db, 'documents', document.id), {
        starred: !document.starred,
        updatedAt: Timestamp.now()
      });
      debugLog('STAR TOGGLE SUCCESS', { documentId: document.id, starred: !document.starred });
    } catch (error) {
      debugLog('STAR TOGGLE ERROR', null, error);
    }
  };

  // Navigation vers un dossier spécifique
  const openFolder = (categoryKey) => {
    setCurrentFolder(categoryKey);
    setCurrentView('documents');
    setSelectedCategory(categoryKey);
  };

  // Retour à la vue dossiers
  const backToFolders = () => {
    setCurrentView('folders');
    setCurrentFolder(null);
    setSelectedCategory('all');
  };

  // Afficher tous les documents
  const showAllDocuments = () => {
    setCurrentView('all');
    setCurrentFolder(null);
    setSelectedCategory('all');
  };

  // Gestion du drag & drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  // Filtrage des documents avec nouveaux filtres
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesFavorites = !showFavoritesOnly || doc.starred;
    const matchesMyDocs = !showMyDocsOnly || doc.uploadedBy === auth.currentUser?.uid;
    
    return matchesSearch && matchesCategory && matchesFavorites && matchesMyDocs;
  });

  // Utilitaires de formatage
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (document) => {
    if (document.type?.startsWith('image/')) return <Image className="w-6 h-6" />;
    if (document.type === 'application/pdf') return <FileText className="w-6 h-6" />;
    return <File className="w-6 h-6" />;
  };

  // Fonction pour télécharger un fichier directement - Version Firebase optimisée
  const downloadFile = async (document) => {
    try {
      debugLog('DOWNLOAD START', { documentId: document.id, url: document.url });
      
      // Méthode 1 : Essayer avec l'API moderne
      if ('showSaveFilePicker' in window) {
        try {
          const response = await fetch(document.url, {
            mode: 'cors',
            headers: {
              'Access-Control-Allow-Origin': '*',
            }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            
            // API File System moderne (Chrome/Edge récents)
            const fileHandle = await window.showSaveFilePicker({
              suggestedName: document.originalName || document.name,
              types: [{
                description: 'Fichiers',
                accept: { [document.type || 'application/octet-stream']: [`.${document.name.split('.').pop()}`] }
              }]
            });
            
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            
            debugLog('DOWNLOAD SUCCESS (Modern API)', document.name);
            return;
          }
        } catch (modernError) {
          debugLog('MODERN API FAILED', null, modernError);
          // Continue vers les autres méthodes
        }
      }
      
      // Méthode 2 : Téléchargement classique avec iframe caché
      const fileName = document.originalName || document.name;
      
      // Créer un iframe caché pour le téléchargement
      const iframe = window.document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      iframe.src = document.url;
      
      // Ajouter au DOM temporairement
      document.body.appendChild(iframe);
      
      // Supprimer l'iframe après un délai
      setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 2000);
      
      debugLog('DOWNLOAD INITIATED (Iframe method)', document.name);
      
      // Optionnel : incrémenter le compteur de téléchargements
      try {
        await updateDoc(doc(db, 'documents', document.id), {
          downloadCount: (document.downloadCount || 0) + 1,
          lastDownloadAt: Timestamp.now()
        });
      } catch (updateError) {
        debugLog('DOWNLOAD COUNT UPDATE ERROR', null, updateError);
      }
      
    } catch (error) {
      debugLog('DOWNLOAD ERROR', null, error);
      
      // Méthode 3 : Fallback ultime - Créer un lien avec download + force download URL
      try {
        // Modifier l'URL Firebase pour forcer le téléchargement
        const downloadUrl = document.url.includes('?') 
          ? `${document.url}&response-content-disposition=attachment%3B%20filename%3D"${encodeURIComponent(document.originalName || document.name)}"`
          : `${document.url}?response-content-disposition=attachment%3B%20filename%3D"${encodeURIComponent(document.originalName || document.name)}"`;
        
        const link = window.document.createElement('a');
        link.href = downloadUrl;
        link.download = document.originalName || document.name;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Déclencher le clic
        link.click();
        
        debugLog('DOWNLOAD FALLBACK', document.name);
        
      } catch (fallbackError) {
        debugLog('ALL DOWNLOAD METHODS FAILED', null, fallbackError);
        
        // Dernier recours : notification à l'utilisateur
        alert(`Pour télécharger "${document.name}", cliquez avec le bouton droit sur ce lien et sélectionnez "Enregistrer sous"`);
        window.open(document.url, '_blank');
      }
    }
  };

  // Modal de sélection de catégorie
  const CategorySelectionModal = () => {
    if (!showCategoryModal || pendingFiles.length === 0) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Tag className="w-5 h-5 mr-2" />
                Catégoriser les fichiers
              </h3>
              <button 
                onClick={cancelUpload}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                {pendingFiles.length} fichier(s) à uploader :
              </p>
              <ul className="text-sm text-gray-500 max-h-20 overflow-y-auto">
                {pendingFiles.map((file, index) => (
                  <li key={index} className="truncate">• {file.name}</li>
                ))}
              </ul>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez une catégorie :
              </label>
              <select
                value={selectedCategoryForUpload}
                onChange={(e) => setSelectedCategoryForUpload(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => {
                  const IconComponent = category.icon;
                  return (
                    <option key={key} value={key}>
                      {category.name}
                    </option>
                  );
                })}
              </select>
              
              <div className="mt-2 p-2 bg-gray-50 rounded flex items-center">
                {(() => {
                  const selectedCat = DOCUMENT_CATEGORIES[selectedCategoryForUpload];
                  const IconComponent = selectedCat.icon;
                  return (
                    <>
                      <IconComponent className={`w-4 h-4 mr-2 text-${selectedCat.color}-500`} />
                      <span className="text-sm text-gray-600">{selectedCat.name}</span>
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelUpload}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmUpload}
                disabled={uploading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Upload...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Uploader
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal de prévisualisation
  const PreviewModal = () => {
    if (!showPreview || !selectedDocument) return null;

    const isImage = selectedDocument.type?.startsWith('image/');
    const isPDF = selectedDocument.type === 'application/pdf';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full mx-4 h-5/6 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{selectedDocument.name}</h3>
            <button 
              onClick={() => setShowPreview(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 p-4 overflow-auto">
            {isImage ? (
              <img 
                src={selectedDocument.url} 
                alt={selectedDocument.name}
                className="max-w-full h-auto mx-auto"
              />
            ) : isPDF ? (
              <iframe 
                src={selectedDocument.url}
                className="w-full h-full border-0"
                title={selectedDocument.name}
              />
            ) : (
              <div className="text-center py-8">
                <File className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Aperçu non disponible pour ce type de fichier</p>
                <a 
                  href={selectedDocument.url}
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFile(selectedDocument);
                  }}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Vue des dossiers par catégorie
  const FoldersView = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Centre de documents</h2>
        <p className="text-gray-600">Organisés par catégorie pour une meilleure navigation</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => {
          const IconComponent = category.icon;
          const count = categoryStats[key] || 0;
          
          return (
            <div
              key={key}
              onClick={() => openFolder(key)}
              className={`
                p-6 bg-white border-2 border-${category.color}-200 rounded-lg 
                hover:border-${category.color}-400 hover:shadow-lg cursor-pointer 
                transition-all duration-200 group
              `}
            >
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-${category.color}-100 rounded-lg mb-4 group-hover:bg-${category.color}-200 transition-colors`}>
                  <IconComponent className={`w-8 h-8 text-${category.color}-600`} />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {category.name}
                </h3>
                
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <span>{count}</span>
                  <span>{count <= 1 ? 'document' : 'documents'}</span>
                </div>
                
                {count > 0 && (
                  <div className={`mt-3 inline-flex items-center text-sm text-${category.color}-600 group-hover:text-${category.color}-700`}>
                    Ouvrir le dossier
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions rapides */}
      <div className="flex items-center justify-center space-x-4 mt-8">
        <button
          onClick={showAllDocuments}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <List className="w-4 h-4 mr-2" />
          Voir tous les documents ({documents.length})
        </button>
      </div>
    </div>
  );

  // Interface principale
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Affichage des erreurs */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Erreur</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Zone d'upload avec sélection de catégorie */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex items-center justify-center mb-4">
          <CloudUpload className={`w-12 h-12 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
        </div>
        
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {dragActive ? 'Déposez vos fichiers ici' : 'Ajouter des documents'}
        </h3>
        
        <p className="text-gray-600 mb-4">
          Glissez-déposez vos fichiers ou{' '}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-blue-600 hover:text-blue-700 font-medium"
            disabled={uploading}
          >
            parcourez votre ordinateur
          </button>
        </p>
        
        <p className="text-sm text-gray-500 mb-4">
          Vous pourrez choisir la catégorie avant l'upload
        </p>
        
        {uploading && (
          <div className="mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-blue-600">Upload en cours...</p>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => handleFileUpload(e.target.files)}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
      </div>

      {/* Navigation et fil d'Ariane */}
      {(currentView === 'documents' || currentView === 'all') && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            {/* Fil d'Ariane */}
            <div className="flex items-center space-x-2 text-sm">
              <button
                onClick={backToFolders}
                className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                <Home className="w-4 h-4 mr-1" />
                Dossiers
              </button>
              
              {currentView === 'documents' && currentFolder && (
                <>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {DOCUMENT_CATEGORIES[currentFolder]?.name || 'Dossier'}
                  </span>
                </>
              )}
              
              {currentView === 'all' && (
                <>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Tous les documents</span>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              {currentView === 'documents' && (
                <button
                  onClick={showAllDocuments}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Voir tous
                </button>
              )}
              
              <button
                onClick={backToFolders}
                className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Retour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      {currentView === 'folders' ? (
        <FoldersView />
      ) : (
        <>
          {/* Filtres étendus pour la vue documents */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex flex-col space-y-4">
              {/* Première ligne : Recherche */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                {/* Barre de recherche */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Rechercher des documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Mode d'affichage et actualiser */}
                <div className="flex items-center space-x-2">
                  {/* Mode d'affichage */}
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50'}`}
                      title="Vue grille"
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50'}`}
                      title="Vue liste"
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Actualiser */}
                  <button
                    onClick={() => window.location.reload()}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Actualiser"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Deuxième ligne : Filtres */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
                {/* Catégories (seulement en vue "tous") */}
                {currentView === 'all' && (
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Catégorie :</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="all">Toutes</option>
                      {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => (
                        <option key={key} value={key}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Filtres spéciaux */}
                <div className="flex items-center space-x-4">
                  {/* Favoris uniquement */}
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showFavoritesOnly}
                      onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex items-center space-x-1 text-sm text-gray-700">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span>Favoris uniquement</span>
                    </div>
                  </label>

                  {/* Mes documents uniquement */}
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showMyDocsOnly}
                      onChange={(e) => setShowMyDocsOnly(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex items-center space-x-1 text-sm text-gray-700">
                      <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                      <span>Mes documents</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Statistiques */}
              <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t">
                <span>
                  {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} 
                  {currentView === 'documents' && currentFolder && (
                    ` dans ${DOCUMENT_CATEGORIES[currentFolder]?.name || 'ce dossier'}`
                  )}
                </span>
                <span>Total : {documents.length} documents</span>
              </div>
            </div>
          </div>

          {/* Liste des documents */}
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <FolderOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {currentView === 'documents' && currentFolder 
                  ? `Aucun document dans ${DOCUMENT_CATEGORIES[currentFolder]?.name || 'ce dossier'}`
                  : 'Aucun document trouvé'
                }
              </h3>
              <p className="text-gray-600">
                {searchTerm || selectedCategory !== 'all' || showFavoritesOnly || showMyDocsOnly
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Commencez par ajouter des documents à votre projet'
                }
              </p>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                : 'space-y-2'
            }>
              {filteredDocuments.map((document) => {
                const categoryInfo = DOCUMENT_CATEGORIES[document.category] || DOCUMENT_CATEGORIES.other;
                const canDelete = canDeleteDocument(document);
                
                return (
                  <div key={document.id} className="bg-white rounded-lg border hover:shadow-md transition-shadow">
                    {viewMode === 'grid' ? (
                      <>
                        {/* Vue grille */}
                        <div className="aspect-video bg-gray-100 rounded-t-lg flex items-center justify-center relative">
                          {document.type?.startsWith('image/') ? (
                            <img 
                              src={document.url} 
                              alt={document.name}
                              className="w-full h-full object-cover rounded-t-lg"
                            />
                          ) : (
                            <div className={`text-${categoryInfo.color}-500`}>
                              {getFileIcon(document)}
                            </div>
                          )}
                          
                          <div className="absolute top-2 right-2">
                            <span className={`px-2 py-1 text-xs rounded-full bg-${categoryInfo.color}-100 text-${categoryInfo.color}-800`}>
                              {categoryInfo.name}
                            </span>
                          </div>
                          
                          {document.starred && (
                            <div className="absolute bottom-2 left-2">
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            </div>
                          )}

                          {/* Indicateur si c'est son document */}
                          {document.uploadedBy === auth.currentUser?.uid && (
                            <div className="absolute bottom-2 right-2">
                              <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                                Mes docs
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-4">
                          <h3 className="font-medium text-gray-900 truncate" title={document.name}>
                            {document.name}
                          </h3>
                          
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm text-gray-500">{formatFileSize(document.size)}</span>
                          </div>
                          
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(document.createdAt)}
                          </p>

                          {/* Info sur l'uploader */}
                          <p className="text-xs text-gray-400 mt-1">
                            Par: {document.uploadedByEmail?.split('@')[0] || 'Utilisateur'}
                          </p>
                          
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedDocument(document);
                                  setShowPreview(true);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600"
                                title="Aperçu"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              
                              <button
                                onClick={() => downloadFile(document)}
                                className="p-1 text-gray-400 hover:text-green-600"
                                title="Télécharger"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              
                              <button
                                onClick={() => toggleStar(document)}
                                className="p-1 text-gray-400 hover:text-yellow-600"
                                title="Favori"
                              >
                                {document.starred ? (
                                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                ) : (
                                  <StarOff className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                            
                            {/* Bouton de suppression avec permissions */}
                            {canDelete && (
                              <button
                                onClick={() => deleteDocument(document)}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title={isAdmin ? "Supprimer (Admin)" : "Supprimer mon document"}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      // Vue liste
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 flex-1">
                            <div className={`text-${categoryInfo.color}-500`}>
                              {getFileIcon(document)}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <h3 className="font-medium text-gray-900 truncate">{document.name}</h3>
                                {document.starred && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                                <span className={`px-2 py-1 text-xs rounded-full bg-${categoryInfo.color}-100 text-${categoryInfo.color}-800`}>
                                  {categoryInfo.name}
                                </span>
                                {/* Indicateur propriétaire */}
                                {document.uploadedBy === auth.currentUser?.uid && (
                                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                    Mes docs
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                                <span>{formatFileSize(document.size)}</span>
                                <span>{formatDate(document.createdAt)}</span>
                                <span>Par: {document.uploadedByEmail?.split('@')[0] || 'Utilisateur'}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => toggleStar(document)}
                              className="p-2 text-gray-400 hover:text-yellow-600"
                              title="Favori"
                            >
                              {document.starred ? (
                                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                              ) : (
                                <StarOff className="w-4 h-4" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => {
                                setSelectedDocument(document);
                                setShowPreview(true);
                              }}
                              className="p-2 text-gray-400 hover:text-blue-600"
                              title="Aperçu"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            
                            <button
                              onClick={() => downloadFile(document)}
                              className="p-2 text-gray-400 hover:text-green-600"
                              title="Télécharger"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            
                            {/* Bouton de suppression avec permissions */}
                            {canDelete && (
                              <button
                                onClick={() => deleteDocument(document)}
                                className="p-2 text-gray-400 hover:text-red-600"
                                title={isAdmin ? "Supprimer (Admin)" : "Supprimer mon document"}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modal de sélection de catégorie */}
      <CategorySelectionModal />

      {/* Modal de prévisualisation */}
      <PreviewModal />
    </div>
  );
};

export default DocumentsManager;